import React, { useState, useEffect } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Resize,
  Sort,
  ContextMenu,
  Filter,
  Page,
  ExcelExport,
  PdfExport,
  Inject,
} from "@syncfusion/ej2-react-grids";
import { Header } from "../Components";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaTrash,
  FaTimes,
  FaFileExcel,
  FaFilePdf,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { BaseUrlProvider, useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

// Grid column configuration
const contactGrid = [
  {
    field: "id",
    headerText: "ID",
    width: 80,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">ID</span>,
  },
  {
    field: "name",
    headerText: "Name",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Name</span>,
  },
  {
    field: "email",
    headerText: "Email",
    width: 180,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Email</span>,
  },
  {
    field: "contact_number",
    headerText: "Contact",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Contact</span>,
  },
  {
    field: "subject",
    headerText: "Subject",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Subject</span>,
  },
  {
    field: "message",
    headerText: "Message",
    width: 200,
    textAlign: "Left",
    allowFiltering: true,
    headerTemplate: () => <span className="font-bold">Message</span>,
  },
  {
    field: "date_time",
    headerText: "Date & Time",
    width: 120,
    format: "yMd",
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Date & Time</span>,
  },
  {
    field: "Actions",
    headerText: "Actions",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex justify-center gap-3">
        <button
          className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.viewHandler(props)}
          title="View Details"
        >
          <FaEye size={18} />
        </button>
        <button
          className="text-red-600 hover:text-red-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.deleteHandler(props.id)}
          title="Delete"
        >
          <FaTrash size={18} />
        </button>
      </div>
    ),
  },
];

const Contact = () => {
  const [contactData, setContactData] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const { baseUrl } = useBaseUrl(); // ✅ Get base URL from context

  // Fetch contact data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setErrorMessage("");
      // const token = localStorage.getItem('authToken');
      // console.log(token)
      // if (!token) {
      //   setErrorMessage('Please log in to access this page.');
      //   navigate('/');
      //   return;
      // }
      try {
        const formData = new FormData();
        formData.append("action", "get-contacts");

        const response = await axios.post(
          `${baseUrl}contact_form.php`,
          formData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              // 'Authorization': 'Bearer your-admin-token',
              // Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        console.log("API Response:", response.data); // Debug log
        if (response.data.status) {
          setContactData(response.data.data || []);
        } else {
          setErrorMessage(
            response.data.error || "Failed to fetch contact data."
          );
        }
      } catch (error) {
        console.error("Axios Error:", error.response || error);
        if (error.response?.status === 401) {
          // setErrorMessage('Session expired. Please log in again.');
          // localStorage.removeItem('authToken');
          navigate("/");
        } else {
          setErrorMessage(
            error.response?.data?.error || "Error connecting to the server."
          );
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Handle delete action
  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this contact entry?")) {
      setIsLoading(true);
      setErrorMessage("");
      // const token = localStorage.getItem('authToken');
      // if (!token) {
      //   setErrorMessage('Please log in to access this page.');
      //   navigate('/');
      //   return;
      // }
      try {
        const formData = new FormData();
        formData.append("action", "delete-contact");
        formData.append("id", id);

        const response = await axios.post(
          `${baseUrl}contact_form.php`,
          formData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              // Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        console.log("Delete Response:", response.data); // Debug log
        if (response.data.status) {
          setContactData(contactData.filter((item) => item.id !== id));
          alert("Contact entry deleted successfully.");
        } else {
          setErrorMessage(response.data.error || "Failed to delete contact.");
        }
      } catch (error) {
        console.error("Error deleting contact:", error.response || error);
        if (error.response?.status === 401) {
          // setErrorMessage('Session expired. Please log in again.');
          // localStorage.removeItem('authToken');
          // navigate('/');
        } else {
          setErrorMessage(
            error.response?.data?.error || "Failed to delete contact entry."
          );
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle view action
  const viewHandler = (contact) => {
    setSelectedContact(contact);
  };

  // Search filter
  const filteredData = contactData.filter(
    (item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Inject handlers into grid data
  const gridData = filteredData.map((item) => ({
    ...item,
    deleteHandler,
    viewHandler,
  }));

  const { setCurrentColor, setCurrentMode, currentMode } = useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, []);

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn bg-gray-100 min-h-screen"
    >
      <Header category="Dashboard" title="Contact Form Submissions" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Search and Export Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or subject..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() =>
              document
                .getElementById("contactGrid")
                .ej2_instances[0].excelExport()
            }
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() =>
              document
                .getElementById("contactGrid")
                .ej2_instances[0].pdfExport()
            }
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="contactGrid"
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          allowExcelExport={true}
          allowPdfExport={true}
          allowFiltering={true}
          allowResizing={true}
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: "Menu", showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {contactGrid.map((item, index) => (
              <ColumnDirective key={index} {...item} />
            ))}
          </ColumnsDirective>
          <Inject
            services={[
              Resize,
              Sort,
              ContextMenu,
              Filter,
              Page,
              ExcelExport,
              PdfExport,
            ]}
          />
        </GridComponent>
      </div>

      {/* Modal for Viewing Contact Details */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Contact Details
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setSelectedContact(null)}
              >
                <FaTimes size={24} />
              </button>
            </div>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>ID:</strong> {selectedContact.id}{" "}
              </p>
              <p>
                <strong>Name:</strong> {selectedContact.name}
              </p>
              <p>
                <strong>Email:</strong> {selectedContact.email}
              </p>
              <p>
                <strong>Contact:</strong> {selectedContact.contact_number}
              </p>
              <p>
                <strong>Subject:</strong> {selectedContact.subject}
              </p>
              <p>
                <strong>Message:</strong> {selectedContact.message}
              </p>
              <p>
                <strong>Date:</strong> {selectedContact.date_time}
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                onClick={() => setSelectedContact(null)}
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                onClick={() => {
                  deleteHandler(selectedContact.id);
                  setSelectedContact(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
